/**
 * React Hook for Push Notification Management
 * Handles FCM token registration and notification settings
 */

import { useState, useEffect, useCallback } from 'react';
import { push } from './api';
import { 
  requestNotificationPermission, 
  onForegroundMessage, 
  getNotificationStatus,
  saveFcmToken,
  getCachedFcmToken
} from './firebase';

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check initial status
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const status = await getNotificationStatus();
        setIsSupported(status.supported);
        setPermission(status.permission);
        
        // Check if we have a token registered with the backend
        if (status.supported && status.permission === 'granted') {
          const backendStatus = await push.status();
          setIsEnabled(backendStatus.data.enabled);
        }
      } catch (err) {
        console.error('Error checking notification status:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
  }, []);

  // Set up foreground message listener
  useEffect(() => {
    let unsubscribe = null;

    const setupListener = async () => {
      if (isEnabled) {
        unsubscribe = await onForegroundMessage((payload) => {
          // Show browser notification for foreground messages
          if (Notification.permission === 'granted') {
            const { title, body } = payload.notification || {};
            new Notification(title || 'DealShaq', {
              body: body || 'You have a new notification',
              icon: '/logo192.png',
              tag: 'dealshaq-foreground',
              data: payload.data
            });
          }
        });
      }
    };

    setupListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [isEnabled]);

  // Enable push notifications
  const enableNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Request permission and get token
      const token = await requestNotificationPermission();
      
      if (!token) {
        throw new Error('Failed to get notification permission or token');
      }

      // Register token with backend
      await push.register(token);
      
      // Cache token locally
      saveFcmToken(token);
      
      setIsEnabled(true);
      setPermission('granted');
      
      return { success: true };
    } catch (err) {
      console.error('Error enabling notifications:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Disable push notifications
  const disableNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getCachedFcmToken();
      
      if (token) {
        // Unregister from backend
        await push.unregister(token);
        
        // Clear local cache
        saveFcmToken(null);
      }
      
      setIsEnabled(false);
      
      return { success: true };
    } catch (err) {
      console.error('Error disabling notifications:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    try {
      const response = await push.test();
      return response.data;
    } catch (err) {
      console.error('Error sending test notification:', err);
      throw err;
    }
  }, []);

  return {
    isSupported,
    isEnabled,
    permission,
    loading,
    error,
    enableNotifications,
    disableNotifications,
    sendTestNotification
  };
}

export default usePushNotifications;
